import { CopyIconButton } from '@components/button';
import { Form, FormFileUpload, FormTextInput, StandardFormButtons } from '@components/form';
import { Group } from '@components/layout';
import { ExternalLink } from '@components/link';
import { Typography } from '@components/typography';
import { GetSSHKeysDocument, useCreateSSHKeyMutation } from '@generated/graphql';
import { Divider, Stack } from '@mui/material';
import * as yup from 'yup';

const validationSchema = yup
  .object({
    name: yup.string().required('Name is required'),
    keyFile: yup.mixed(),
    keyString: yup.string(),
  })
  .test('key', 'Key is required', (value) => Boolean(value.keyFile || value.keyString));

export const AddSSHKeyModal = ({ onClose }: { onClose: () => void }) => {
  const [addSSHKeyMutation] = useCreateSSHKeyMutation();
  const handleFormSubmit = async (values: {
    name: string;
    keyFile: unknown;
    keyString: string;
  }) => {
    const variables = { name: values.name } as {
      name: string;
      key: string;
    };

    if (values.keyString) {
      variables.key = values.keyString;
    } else if (values.keyFile) {
      let file: File | null = null;

      if (values.keyFile instanceof File) {
        file = values.keyFile;
      } else if (values.keyFile instanceof FileList || Array.isArray(values.keyFile)) {
        file = values.keyFile[0] as File;
      } else if (typeof values.keyFile === 'string' && values.keyFile.trim() !== '') {
        variables.key = values.keyFile;
      }

      if (file) {
        try {
          const fileContent = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              if (e.target && typeof e.target.result === 'string') {
                resolve(e.target.result);
              } else {
                reject(new Error('Failed to read file'));
              }
            };
            reader.onerror = () => {
              reject(new Error('Error reading file'));
            };
            reader.readAsText(file);
          });

          variables.key = fileContent.trim();
        } catch (error) {
          console.error('Error reading file:', error);
        }
      } else {
        console.error('File object not found in values.key');
      }
    } else {
      console.error('No key field in values:', values);
    }
    await addSSHKeyMutation({
      variables,
      refetchQueries: [{ query: GetSSHKeysDocument }],
      onCompleted: () => {
        onClose?.();
      },
    });
  };

  return (
    <Form
      initialValues={{
        name: '',
        keyFile: '',
        keyString: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {() => {
        return (
          <Stack maxWidth="500px">
            <Typography variant="caption2" sx={{ width: '100%' }}>
              SSH keys allow you to connect your remote server to your local development
              environment. If you need to create a new key you can use this terminal command:
            </Typography>
            <Group
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 2,
                py: 1,
                my: 3,
              }}
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <Typography
                variant="caption2"
                sx={{ width: '100%', fontFamily: 'monospace', color: 'text.primary' }}
              >
                {`ssh-keygen -t ed25519 -C "your_email@example.com"`}
              </Typography>
              <CopyIconButton value={`ssh-keygen -t ed25519 -C "your_email@example.com"`} />
            </Group>
            <Typography variant="caption2" sx={{ width: '100%', mb: 5 }}>
              If you&apos;d like to learn more about SSH keys, you can read more about them{' '}
              <ExternalLink
                href="https://docs.github.com/en/authentication/connecting-to-github-with-ssh/about-ssh"
                target="_blank"
              >
                here
              </ExternalLink>
              .
            </Typography>
            <FormTextInput name="name" label="Name *" />
            <Divider sx={{ mb: 5 }} />
            <FormFileUpload name="keyFile" label="public key file" accept=".pub" />
            <Typography variant="caption2" sx={{ width: '100%', textAlign: 'center' }}>
              or
            </Typography>
            <FormTextInput name="keyString" label="Paste public key string" />
            <StandardFormButtons onClose={onClose} submitLabel="Add key" />
          </Stack>
        );
      }}
    </Form>
  );
};
